import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {Breadcrumb, Button, Space, Dropdown, Input, Layout, Select, Table, theme, Tooltip} from "antd";
import { DownOutlined, FileDoneOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { apiGetVirtualAccountDetails, apiListVirtualAccountEntries, apiEntriesDownload } from "../../../api/account";
import { ReactComponent as Money } from "../../../assets/images/account/money.svg";
import { ReactComponent as MoneyDown } from "../../../assets/images/account/table-money-down.svg";
import { ReactComponent as MoneyUp } from "../../../assets/images/account/table-money-up.svg";
import { formatDate, formatPriceInCents } from "../../../utils/format";
import { accountEntryStatusChoices } from "../../../utils/optionsEvent";
import { useIsMobileHook } from "../../../utils/WindowHooks";
import DepositModal from "../../common/DepositModal/DepositModal";
import TransferModal from "../../common/TransferModal/TransferModal";
import TransferModalBoleto from "../../common/TransferModalBoleto/TransferModalBoleto";
import './Account.scss';

const Account = () => {  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [habilityBalance, setHabilityBalance] = useState(false);
  const [transactions, setTransactions] = useState({ results: [], count: 0 });  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [openTransferBoletoModal, setOpenTransferBoletoModal] = useState(false);
  const [openDepositModal, setOpenDepositModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleSortChange = (value) => {
    setSortedInfo({
      order: value,
      columnKey: 'value',
    });
  };  // Map data with proper keys for table (no filtering since search is server-side)
  const filteredData = React.useMemo(() => {
    if (!transactions?.results) return [];
    
    return transactions.results.map((item, index) => ({
      ...item,
      key: item.id || `${item.name}-${index}`, // Ensure each row has a unique key
    }));
  }, [transactions?.results]);

  const handleDownload = async (format) => {
      try {
        const blob = await apiEntriesDownload(format);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `historico-movimentacoes.${format === 'csv' ? 'csv' : 'xlsx'}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Failed to download file:', error);
      }
    };

  const menuProps = {
    items: [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ],
    onClick: e => handleDownload(e.key),
  };

  const columns = [
    {
      title: 'Nome da transação',
      dataIndex: 'name',
      key: 'name',
      render: (transactionName, { kind }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {kind === 'OUT' ? <MoneyDown style={{ marginRight: 8 }} /> : <MoneyUp style={{ marginRight: 8 }} />}
          <span>{transactionName}</span>
        </div>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a, b) => {
        const amountA = parseFloat(a.replace('R$ ', '').replace(/\./g, '').replace(',', '.'));
        const amountB = parseFloat(b.replace('R$ ', '').replace(/\./g, '').replace(',', '.'));
        return amountA - amountB;
      },
      render: (amount) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{formatPriceInCents(amount)}</span>
        </div>
      ),
      sortOrder: sortedInfo.columnKey === 'amount' ? sortedInfo.order : null,
      responsive: ["md"],
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      sortOrder: sortedInfo.columnKey === 'date' ? sortedInfo.order : null,
      render: (created_at) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{formatDate(created_at)}</span>
        </div>
      ),
      responsive: ["md"],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, item) => {
        const statusObj = accountEntryStatusChoices.find(choice => choice.value === status);
        return <Tooltip placement="topLeft" title={item.status_reason}>
          {statusObj ? (
            <span className={`status-color ${statusObj.label}`}>{statusObj.label}</span>
          ) : (
            <span className="status-color unknown">Desconhecido</span>
          )}
        </Tooltip>
      },
    },
  ];

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const isMobile = useIsMobileHook(1100);
  const handleRowClick = (record) => {
    const id = record.key;
    if (expandedRowKeys.includes(id)) {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== id));
    } else {
      setExpandedRowKeys([id]);
    }
  };
  const expandedRowRender = (record) => {
    const statusObj = accountEntryStatusChoices.find(choice => choice.value === record.status);
    return (
      <div className="expanded">
        <div className="ticket-actions">
          <p><strong>Nome da transação:</strong> {record.name}</p>
          <p><strong>Valor:</strong> {formatPriceInCents(record.amount)}</p>
          <p><strong>Data:</strong> {formatDate(record.created_at)}</p>
          <p><strong>Status:</strong> <span className={`status-color ${statusObj?.label || 'unknown'}`}>{statusObj?.label || 'Desconhecido'}</span></p>
        </div>
      </div>
    );
  };

  const [dataAccount, setDataAccount] = useState({
    balance: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [accountDetails, entries] = await Promise.all([
          apiGetVirtualAccountDetails(),
          apiListVirtualAccountEntries(currentPage, pageSize, debouncedSearchText),
        ]);

        setDataAccount({ balance: accountDetails.balance });
        setTransactions(entries);      } catch (err) {
        console.error('Erro ao buscar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [openTransferModal, openTransferBoletoModal, openDepositModal, currentPage, pageSize, debouncedSearchText])

  return (
    <div className='Account' style={{ backgroundColor: "#f5f5f5" }}>
      <Helmet>
        <title>Clap Tickets - Conta</title>
      </Helmet>
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Content
          className="content-dashboard"
          style={{
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Breadcrumb
            className="breadcrumb-title"
          >
            <Breadcrumb.Item >Minha conta</Breadcrumb.Item>
          </Breadcrumb>

          <div className="container-account">
            <div className="top-account">
              <div className="card-balance">
                <span>Saldo da conta</span>
                <div className="value-balance">
                  <div className="balance-container">
                    {habilityBalance && !loading ? <p className={`money ${habilityBalance ? "" : "hidden"}`}>{formatPriceInCents(dataAccount?.balance)}</p> : <p className="empty-money">••••</p>}
                    {/* {!habilityBalance && <p className="empty-money">••••</p>} */}
                  </div>
                  {!habilityBalance && <EyeOutlined onClick={() => setHabilityBalance(true)} />}
                  {habilityBalance && <EyeInvisibleOutlined onClick={() => setHabilityBalance(false)} />}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', gap: 10 }}>
                <button className="money-transfer" onClick={() => setOpenTransferModal(true)}>
                  <Money /> Transferir saldo
                </button>
                <button className="money-transfer" onClick={() => setOpenTransferBoletoModal(true)}>
                  <Money /> Pagar Boleto
                </button>
                <button className="money-transfer" onClick={() => setOpenDepositModal(true)}>
                  <Money /> Depositar
                </button>
              </div>
            </div>

            <div className="account-body">
              <div className="account-filters">
                <div className="title">
                  <h2>Histórico de movimentações</h2>
                </div>
                <div className="filters-div">
                  <Input.Search
                    placeholder="Pesquisar transação"
                    onSearch={handleSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ width: 300, marginBottom: 20 }}
                  />
                </div>
              </div>              <Table
                columns={columns}
                dataSource={filteredData}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: transactions?.count || 0,
                  showSizeChanger: false,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    if (size !== pageSize) {
                      setPageSize(size);
                    }
                  },
                  onShowSizeChange: (current, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                  },
                }}
                loading={loading}
                rowKey="key"
                expandable={{
                  expandedRowRender: isMobile ? expandedRowRender : undefined,
                  expandedRowKeys: expandedRowKeys,
                  onExpand: (expanded, record) => handleRowClick(record),
                  responsive: ["xl"],
                }}
              />
            </div>
          </div>
          <div className="download-section">
            <Dropdown menu={menuProps} >
              <Button>
                <Space>
                  Baixar Relatório
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </Content>
      </Layout>
      <TransferModal visible={openTransferModal} onClose={() => setOpenTransferModal(false)} bankBalance={dataAccount?.balance} />
      <TransferModalBoleto visible={openTransferBoletoModal} onClose={() => setOpenTransferBoletoModal(false)} bankBalance={dataAccount?.balance} />
      <DepositModal visible={openDepositModal} onClose={() => setOpenDepositModal(false)} />
    </div>
  )
}

export default Account
